import React, { useEffect, useMemo, useState } from 'react';
import { Button, Typography, Grid, Table, Tooltip, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { getOrders, getInvoicePdf } from '../../http/orders';
import moment from 'moment/moment';

const OrdersTable = ({ language }) => {
  const breakpoints = Grid.useBreakpoint();
  const { t } = useTranslation();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const downloadInvoice = async (orderId) => {
    setDownloadingId(orderId);
    try {
      const pdf = await getInvoicePdf(orderId);
      const url = window.URL.createObjectURL(pdf);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${orderId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      message.error(t('etc.invoice.error'));
    } finally {
      setDownloadingId(null);
    }
  };

  useEffect(() => {
    setLoading(true);
    getOrders()
      .then(setOrders)
      .finally(() => setLoading(false));
  }, []);

  const columns = useMemo(() => {
    return [
      {
        title: t('etc.orderId'),
        dataIndex: 'id',
        key: 'id',
        ellipsis: {
          showTitle: false
        },
        render: (_, { id }) => (
          <Typography.Text key={id} copyable code>
            {id}
          </Typography.Text>
        )
      },
      {
        title: t('etc.sum'),
        dataIndex: 'sum',
        key: 'sum',
        ellipsis: {
          showTitle: false
        },
        render: (_, { sum }) => (
          <Typography key={sum} style={{ fontWeight: 400, fontSize: 16 }}>
            {sum?.toFixed(2)}
          </Typography>
        )
      },
      {
        title: t('etc.status'),
        dataIndex: 'status',
        key: 'status',
        ellipsis: {
          showTitle: false
        },
        render: (_, { status }) => (
          <Typography.Text
            key={status}
            style={{ fontWeight: 400, fontSize: 16 }}
          >
            {status}
          </Typography.Text>
        )
      },
      {
        title: t('etc.date'),
        dataIndex: 'date',
        key: 'date',
        defaultSortOrder: 'descend',
        responsive: ['md'],
        ellipsis: {
          showTitle: false
        },
        sorter: (a, b) => new Date(a.date) - new Date(b.date),
        render: (date) => {
          let tooltipDate = null,
            innerDate = null;
          if (date) {
            tooltipDate = moment(date).format('LLL');
            innerDate = moment(date, 'YYYY-MM-DD HH:mm:ss.SSSZ').fromNow();
          }
          return (
            <Tooltip placement='left' title={tooltipDate}>
              <Typography.Text style={{ fontWeight: 400, fontSize: 16 }}>
                {innerDate}
              </Typography.Text>
            </Tooltip>
          );
        }
      },
      {
        title: t('etc.invoice.title'),
        dataIndex: 'orderId',
        key: 'invoice',
        render: (_, { orderId, status }) =>
          status === 'confirmed' ? (
            <Button
              size='small'
              loading={downloadingId === orderId}
              onClick={() => downloadInvoice(orderId)}
            >
              {t('etc.invoice.download')}
            </Button>
          ) : null
      }
    ];
  }, [orders, breakpoints, language, downloadingId]);

  const dataSource = useMemo(() => {
    return orders.map((i) => ({
      key: i.id,
      orderId: i.id,
      id: i.t_order_id,
      date: i.createdAt,
      status: i.status,
      sum: i.totalAmount
    }));
  }, [orders]);

  return (
    <Table
      size={breakpoints.lg ? 'large' : 'small'}
      columns={columns}
      loading={loading}
      pagination={{
        position: ['none', 'bottomCenter']
      }}
      dataSource={dataSource}
    />
  );
};

export default OrdersTable;
